import BoardColumnCards from "./ui/BoardColumnCards";
import BoardViewport from "./ui/BoardViewport";
import CommercialFilterBar from "./CommercialFilterBar";
import { useCommercialFilters } from "./CommercialFilterContext";
import { commercialRange, filterCommercialPipeline } from "./commercialFilters";
import { isInRange, validateReportRange } from "./reports/crmReportMetrics";
import { useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import LeadFormDialog from "./LeadFormDialog";
import EnviarWADialog from "./EnviarWADialog";
import ContactoDialog from "./ContactoDialog";
import AgendarAccionDialog from "./AgendarAccionDialog";
import LeadPipelineCard from "./LeadPipelineCard";
import SectionHeader from "./ui/SectionHeader";
import { stageColor } from "./theme/brand";
import { calcularEstadoSuscripcion, diasRestantes } from "./subscriptions/utils";
import { etapaLabel, ETAPA_VENTA_CERRADA, valorEtapaEmbudo } from "./pipelineUtils";

const leadSearchText = lead => [lead.nombre, lead.telefono, String(lead.telefono || "").replace(/\D/g, ""), lead.cursoInteres, lead.observaciones].filter(Boolean).join(" ");
const EMPTY_LEADS = [];

export default function LeadsPipeline({ store, onNuevaVenta }) {
  const { filters, setFilters } = useCommercialFilters();
  const range = useMemo(() => commercialRange(filters, store), [filters, store]);
  const rangeError = validateReportRange(range.from, range.to);
  const visible = useMemo(() => filterCommercialPipeline(store, filters, range), [store, filters, range]);
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [mostrarOcultas, setMostrarOcultas] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuLeadId, setMenuLeadId] = useState(null);
  const [waLead, setWaLead] = useState(null);
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  const [dragOverEtapa, setDragOverEtapa] = useState(null);
  const [contactoLeadId, setContactoLeadId] = useState(null);
  const [snackMsg, setSnackMsg] = useState("");
  const agendarSnapshot = useRef(null);
  const [agendarLeadId, setAgendarLeadId] = useState(null);

  // Existing leads in an archived stage remain reachable after sync or a sale.
  const ocultasConLeads = store.config.etapasCatalogo.filter(e => !e.activa && visible.leads.some(l => l.etapa === e.id));
  const etapas = store.config.etapasCatalogo.filter(e => e.activa || (mostrarOcultas && ocultasConLeads.includes(e))).map(e => e.id);
  const leadsPorEtapa = visible.leadsPorEtapa;

  // Mapa de suscripciones más urgente por lead
  const subsPorLead = {};
  store.ventas.filter(v => !v.anulada && v.endDate && v.leadId).forEach(v => {
    const estado = calcularEstadoSuscripcion(v);
    if (estado === "no_aplica") return;
    const dias = diasRestantes(v);
    if (!subsPorLead[v.leadId] || dias < (subsPorLead[v.leadId].dias ?? Infinity)) {
      subsPorLead[v.leadId] = { estado, dias, productoNombre: v.productoNombre };
    }
  });

  const handleMoverLead = (nuevaEtapa) => {
    if (menuLeadId) store.moverLeadEtapa(menuLeadId, nuevaEtapa);
    setMenuAnchor(null);
    setMenuLeadId(null);
  };

  const handleOpenAgendar = (leadId) => {
    agendarSnapshot.current = store.leads.find(l => l.id === leadId);
    setAgendarLeadId(leadId);
    setMenuAnchor(null);
    setMenuLeadId(null);
  };

  const handleConfirmarAgendar = (agendarForm) => {
    const lead = store.leads.find(l => l.id === agendarLeadId);
    if (!lead) return;
    const fechaProgramada = agendarForm.modo === "dias"
      ? new Date(Date.now() + agendarForm.dias * 86400000).toISOString().slice(0, 10)
      : agendarForm.fecha;
    if (!fechaProgramada) return;
    store.agregarTarea({
      leadId: agendarLeadId,
      descripcion: agendarForm.descripcion || `Seguimiento a ${lead?.nombre || "lead"}`,
      fechaProgramada,
      tipo: agendarForm.tipo,
    });
    setSnackMsg(`${agendarForm.tipo === "visita" ? "Visita" : agendarForm.tipo === "llamada" ? "Llamada" : "Seguimiento"} agendado para ${lead?.nombre || "lead"} ✓`);
    setAgendarLeadId(null);
  };

  const handleMarcarInscrito = (leadId) => {
    onNuevaVenta(leadId);
    setMenuAnchor(null);
    setMenuLeadId(null);
  };

  // ── Drag & Drop ──
  const handleDragStart = (e, leadId) => {
    setDraggedLeadId(leadId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", leadId);
    requestAnimationFrame(() => { e.target.style.opacity = "0.4"; });
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedLeadId(null);
    setDragOverEtapa(null);
  };

  const handleDragOver = (e, etapa) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverEtapa !== etapa) setDragOverEtapa(etapa);
  };

  const handleDragLeave = (e, etapa) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dragOverEtapa === etapa) setDragOverEtapa(null);
    }
  };

  const handleDrop = (e, etapa) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain");
    if (leadId) store.moverLeadEtapa(leadId, etapa);
    setDragOverEtapa(null);
    setDraggedLeadId(null);
  };

  return (
    <Box>
      <SectionHeader
        title="Pipeline"
        description="Arrastra cada lead a la etapa en la que está. Los montos son estimados, excepto en Venta."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenForm(true)} data-onboarding-id="onb-add-lead-btn">
            Nuevo lead
          </Button>
        }
        sx={{ mb: 2 }}
      />

      <CommercialFilterBar store={store} filters={filters} setFilters={setFilters} pipeline />
      {rangeError && <Alert severity="warning" sx={{ mb: 2 }}>{rangeError}</Alert>}
      {ocultasConLeads.length > 0 && <Button size="small" sx={{ mb: 1 }} onClick={() => setMostrarOcultas(!mostrarOcultas)}>
        {mostrarOcultas ? "Ocultar columnas archivadas" : `Ver ${ocultasConLeads.length} columnas ocultas con leads`}
      </Button>}
      <BoardViewport gap={2}>
        {etapas.map((etapa) => {
          const color = stageColor(etapa);
          return (
          <Box
            key={etapa}
            onDragOver={(e) => handleDragOver(e, etapa)}
            onDragLeave={(e) => handleDragLeave(e, etapa)}
            onDrop={(e) => handleDrop(e, etapa)}
            sx={{
              minWidth: 260, maxWidth: 300, flex: "0 0 260px",
              display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden",
              bgcolor: dragOverEtapa === etapa ? "action.hover" : "background.default",
              border: 1, borderColor: "divider",
              borderRadius: 2, p: 1.5,
              borderTop: `3px solid ${color}`,
              outline: dragOverEtapa === etapa ? `2px dashed ${color}` : "none",
              transition: "background-color 0.15s, outline 0.15s",
            }}
          >
            <Stack spacing={0.25} mb={1.5} sx={{ flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">
                    {etapaLabel(store.config, etapa)}
                  </Typography>
                  <Chip
                    label={leadsPorEtapa[etapa]?.length || 0}
                    size="small"
                    aria-label={`${leadsPorEtapa[etapa]?.length || 0} leads`}
                    sx={{ bgcolor: color, color: "#fff", height: 22 }}
                  />
                </Stack>
              </Stack>
              {(() => {
                const enEtapa = (leadsPorEtapa[etapa] || []).filter(l => store.isOwner !== false || l.vendedorId === store.sellerId);
                const monto = valorEtapaEmbudo(etapa, enEtapa, store.ventasActivas.filter(v => filters.mode === "todo" || isInRange(v.fecha, range.from, range.to)));
                const esVenta = etapa === ETAPA_VENTA_CERRADA;
                return monto > 0 ? (
                  <Typography variant="caption" fontWeight={700} color={esVenta ? "success.main" : "text.secondary"}>
                    {store.config.moneda} {monto.toLocaleString()}
                    {esVenta ? " vendido" : " estimado"}
                  </Typography>
                ) : null;
              })()}
            </Stack>

            <BoardColumnCards
              items={leadsPorEtapa[etapa] || EMPTY_LEADS}
              label={etapaLabel(store.config, etapa)}
              getSearchText={leadSearchText}
              emptyMessage={dragOverEtapa === etapa ? "Suelta aquí" : "Sin leads en esta etapa"}
              renderCard={(lead, idx) => (
                <LeadPipelineCard
                  key={lead.id}
                  lead={lead}
                  vendedor={store.config.vendedores.find(v => v.id === lead.vendedorId)}
                  etapa={etapa}
                  subInfo={subsPorLead[lead.id]}
                  diasEntreSeguimiento={store.config.diasEntreSeguimiento || 0}
                  isDragging={draggedLeadId === lead.id}
                  onDragStart={(e) => handleDragStart(e, lead.id)}
                  onDragEnd={handleDragEnd}
                  onOpenWA={setWaLead}
                  onOpenMenu={(e, id) => { setMenuAnchor(e.currentTarget); setMenuLeadId(id); }}
                  onContactar={setContactoLeadId}
                  onAgendar={handleOpenAgendar}
                  onMarcarInscrito={handleMarcarInscrito}
                  {...(etapa === "nuevo" && idx === 0 ? { "data-onboarding-id": "onb-lead-card" } : {})}
                />
              )}
            />
          </Box>
          );
        })}
      </BoardViewport>

      {/* Menu mover etapa */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setEditData(store.leads.find(l => l.id === menuLeadId)); setOpenForm(true); setMenuAnchor(null); }}>Editar lead / ver compras</MenuItem>
        <MenuItem onClick={() => { onNuevaVenta(menuLeadId); setMenuAnchor(null); }}>Nueva venta / recompra</MenuItem>
        {store.isOwner && <MenuItem onClick={() => { store.archivarLead(menuLeadId); setMenuAnchor(null); setMenuLeadId(null); }}>Archivar cliente</MenuItem>}
        <MenuItem disabled>
          <Typography variant="caption" fontWeight={600}>Mover a:</Typography>
        </MenuItem>
        {store.config.etapasPipeline.map((e) => (
          <MenuItem key={e} onClick={() => handleMoverLead(e)}>
            <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: stageColor(e) }} />
            {etapaLabel(store.config, e)}
          </MenuItem>
        ))}
      </Menu>

      {/* Dialog Agendar */}
      <AgendarAccionDialog
        open={Boolean(agendarLeadId)}
        lead={agendarSnapshot.current}
        unavailable={!store.leads.some(l => l.id === agendarLeadId)}
        initialDays={store.config.diasEntreSeguimiento}
        onClose={() => setAgendarLeadId(null)}
        onConfirm={handleConfirmarAgendar}
      />
      <LeadFormDialog open={openForm} onClose={() => { setOpenForm(false); setEditData(null); }} store={store} editData={editData} onNuevaVenta={onNuevaVenta} />
      <EnviarWADialog open={Boolean(waLead)} onClose={() => setWaLead(null)} store={store} lead={waLead} />
      <ContactoDialog open={Boolean(contactoLeadId)} onClose={() => setContactoLeadId(null)} store={store} leadId={contactoLeadId} />
      <Snackbar open={Boolean(snackMsg)} autoHideDuration={3000} onClose={() => setSnackMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnackMsg("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
